import { PAYMENT_METHOD_TYPE, type PaymentMethodType } from './business';
import { FEATURE_FLAGS, type FeatureFlag } from './feature-flags';

import type { PaymentMethod } from '../models';

export const paymentMethodDisplayOrder: PaymentMethodType[] = [
    PAYMENT_METHOD_TYPE.CASH,
    PAYMENT_METHOD_TYPE.MONIEPOINT_TRANSFER,
    PAYMENT_METHOD_TYPE.MONIEPOINT_TERMINAL,
    PAYMENT_METHOD_TYPE.MONIEPOINT_CARD,
    PAYMENT_METHOD_TYPE.OTHER,
    PAYMENT_METHOD_TYPE.LOYALTY_POINTS,
    PAYMENT_METHOD_TYPE.CUSTOMER_WALLET,
];

export const MONIEPOINT_TERMINAL_PAYMENT_ID = 'moniepoint-terminal';

export const HIDDEN_PAYMENT_METHODS: PaymentMethodType[] = [];

export interface PaymentMethodFlagConfig {
    type: PaymentMethodType;
    // Method is shown when at least one of these flags is enabled.
    flags?: FeatureFlag[];
    // Method is hidden when at least one of these flags is enabled,
    // regardless of `flags` — used for methods superseded by a new flow.
    disabledByFlags?: FeatureFlag[];
}

export const PAYMENT_METHOD_FLAGS: PaymentMethodFlagConfig[] = [
    {
        type: PAYMENT_METHOD_TYPE.MONIEPOINT_TRANSFER,
        flags: [
            FEATURE_FLAGS.ENABLE_COLLECTION_ACCOUNT,
            FEATURE_FLAGS.ENABLE_PAYMENT_FLOW_V2,
        ],
    },
    {
        type: PAYMENT_METHOD_TYPE.MONIEPOINT_CARD,
        flags: [FEATURE_FLAGS.ENABLE_PAYMENT_FLOW_V2],
    },
    {
        type: PAYMENT_METHOD_TYPE.MONIEPOINT_TERMINAL,
        disabledByFlags: [FEATURE_FLAGS.ENABLE_PAYMENT_FLOW_V2],
    },
    {
        type: PAYMENT_METHOD_TYPE.LOYALTY_POINTS,
        flags: [FEATURE_FLAGS.ENABLE_LOYALTY_POINTS],
    },
];

// Split payment can only support the v2/terminal-based bank transfer UX
// (there's no way to fit the v1 collection-account flow into the split
// widget model), so it must require ENABLE_PAYMENT_FLOW_V2 specifically,
// not PAYMENT_METHOD_FLAGS' OR-with-collection-account condition.
export const SPLIT_PAYMENT_METHOD_FLAG_OVERRIDES: Partial<
    Record<PaymentMethodType, FeatureFlag[]>
> = {
    [PAYMENT_METHOD_TYPE.MONIEPOINT_TRANSFER]: [
        FEATURE_FLAGS.ENABLE_PAYMENT_FLOW_V2,
    ],
};

export function getEligiblePaymentMethods(
    methods: PaymentMethod[] | undefined,
    featureFlags: Partial<Record<FeatureFlag, boolean>> | null | undefined,
    flagOverrides?: Partial<Record<PaymentMethodType, FeatureFlag[]>>,
): PaymentMethod[] {
    return (methods ?? []).filter((method) => {
        if (HIDDEN_PAYMENT_METHODS.includes(method.type)) {
            return false;
        }

        const flagConfig = PAYMENT_METHOD_FLAGS.find(
            (config) => config.type === method.type,
        );

        if (flagConfig?.disabledByFlags?.some((flag) => featureFlags?.[flag])) {
            return false;
        }

        const requiredFlags = flagOverrides?.[method.type] ?? flagConfig?.flags;

        if (
            requiredFlags &&
            !requiredFlags.some((flag) => featureFlags?.[flag])
        ) {
            return false;
        }

        return true;
    });
}

export interface PaymentMethodFormData {
    id: string;
    paymentMethodId?: string;
    // Method name saved at charge time, so a paid split still shows a
    // name even if its method later drops out of the eligible list.
    paymentMethodName?: string;
    amount?: number;
    isPaid?: boolean;
    paymentDetails?: Record<string, string | undefined>;
    merchantReference?: string;
}

export const PAYMENT_RESTRICTION_TYPE = {
    ITEM_CATEGORY: 'ITEM_CATEGORY',
    CUSTOMER_GROUP: 'CUSTOMER_GROUP',
    BRANCH: 'BRANCH',
} as const;

export type PaymentRestrictionType =
    (typeof PAYMENT_RESTRICTION_TYPE)[keyof typeof PAYMENT_RESTRICTION_TYPE];
